import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material';
import useAuth from 'hooks/useAuth';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation, useNavigate } from 'react-router';
// project imports
import AnimateButton from 'views/components/extended/AnimateButton';
// third party
import useUserEmail from 'hooks/useUserEmail';
import { useDispatch } from 'react-redux';
import * as actionTypes from 'store/actions';
import * as Yup from 'yup';
import FormContainer from '../inputs/FormContainer';
import Password from '../inputs/Password';
import TextField from '../inputs/TextField';

// ===============================|| JWT LOGIN ||=============================== //

interface Props {
  is2faRequired: boolean;
  setIs2faRequired: Function;
  email?: string;
  password?: string;
}

const JWTLogin = ({ is2faRequired, setIs2faRequired, email, password }) => {
  const dispatch = useDispatch();
  const { login } = useAuth();
  const { emailCheckVerify } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const [checked, setChecked] = React.useState(true);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [checkvalidMail, setCheckvalidMail] = React.useState(false);
  const [emailVerifyStatus, setEmailVerifyStatus] = React.useState(!!email);
  const [emailVerifyButton, setEmailVerifyButton] = React.useState(!email);
  const [emailVerifyStatusOther, setEmailVerifyButtonOther] = React.useState(false);
  const [selectedOption, setSelectedOption] = React.useState('');
  const [otpSent, setOtpSent] = React.useState(false);
  const queryParams = new URLSearchParams(location.search);
  const nextURL = queryParams.get('next');
  const [isIdleMessageOpen, setIsIdleMessageOpen] = React.useState(true);
  const isLoggedOutFromIdle = queryParams.has('idle') && queryParams.get('idle') === '1';
  const [isNousSupport, setIsNousSupport] = React.useState(false);
  const users = useUserEmail();
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(intl.formatMessage({ defaultMessage: 'Must be a valid email' }))
      .max(255)
      .required(intl.formatMessage({ defaultMessage: 'Required' })),
    password: Yup.string()
      .max(255)
      .required(intl.formatMessage({ defaultMessage: 'Required' })),
    login2fa: Yup.string().max(255)
  });

  type FormValues = Yup.InferType<typeof validationSchema>;

  const validationSchemaEmail = Yup.object().shape({
    email: Yup.string()
      .email(intl.formatMessage({ defaultMessage: 'Must be a valid email' }))
      .max(255)
      .required(intl.formatMessage({ defaultMessage: 'Required' }))
  });

  type FormValuesEmail = Yup.InferType<typeof validationSchemaEmail>;

  const initialValues = {
    email: email || '',
    password: password || '',
    login2fa: ''
  };

  const onFormSubmit = useCallback(
    async (values: FormValues) => {
      try {
        setIsSubmitting(true);
        let response;
        if (selectedOption !== '') response = await login(values.email, values.password, values.login2fa, '', selectedOption);
        else response = await login(values.email, values.password, values.login2fa);
        const {
          data: { preferredLanguage }
        } = response;

        if (preferredLanguage === 'french') {
          dispatch({ type: actionTypes.THEME_LOCALE, locale: 'fr' });
          localStorage.setItem('language', 'fr');
        } else if (['english-CA', 'english'].includes(preferredLanguage)) {
          dispatch({ type: actionTypes.THEME_LOCALE, locale: 'en-CA' });
          localStorage.setItem('language', 'en-CA');
        } else {
          dispatch({ type: actionTypes.THEME_LOCALE, locale: 'en' });
          localStorage.setItem('language', 'en');
        }

        if (nextURL) navigate(decodeURIComponent(nextURL));
      } catch (e: any) {
        if (e.response?.data.otpSent) {
          setOtpSent(true);
        } else {
          setOtpSent(false);
        }

        if (e.response?.data?.isNousSupport) {
          setIsNousSupport(true);
        }

        if (e.response?.data?.login2fa) {
          setIs2faRequired(true);
        } else {
          setError(e.response?.data?.message ?? 'Unable to login');
        }
        setIsSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [login, nextURL, selectedOption]
  );

  const changeHandler = async (data: any) => {
    try {
      setEmailVerifyStatus(false);
      let as: any = await emailCheckVerify({ email: data.email, lanType: navigator.language.startsWith('en') ? '1' : '0' });
      setEmailVerifyButton(true);
      setEmailVerifyButtonOther(true);
      setCheckvalidMail(false);
      if (as?.isValid) {
        setEmailVerifyButton(false);
        setError(undefined);
        setEmailVerifyStatus(true);
      } else {
        // setEmailVerifyButton(false);
        setEmailVerifyStatus(false);
        setError(as?.msg);
      }
    } catch (e: any) {
      if (e.response?.data?.login2fa) {
        setIs2faRequired(true);
      } else {
        setError(e.response?.data?.message ?? 'Unable to login');
      }
      setIsSubmitting(false);
    }
  };

  const emailCheck = useCallback(
    (values: FormValuesEmail) => {
      // type values = Yup.InferType<typeof validationSchema>;
      setCheckvalidMail(true);
      // setEmailVerifyButton(true);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      changeHandler(values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const checkEmailFirst = () => {
    setEmailVerifyButton(true);
    setEmailVerifyStatus(false);
    setError(undefined);
  };

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };

  return (
    <FormContainer
      validation={emailVerifyButton ? validationSchemaEmail : validationSchema}
      defaultValues={initialValues}
      onSuccess={emailVerifyButton ? emailCheck : onFormSubmit}
    >
      <Stack sx={{ mb: 3 }} spacing={3}>
        {isLoggedOutFromIdle && isIdleMessageOpen && (
          <Alert severity="info" onClose={() => setIsIdleMessageOpen(false)}>
            <FormattedMessage id="login.errorMessage.idle" defaultMessage="Your session has expired." />
          </Alert>
        )}
        {!is2faRequired && (
          <>
            <TextField name="email" onKeyUp={checkEmailFirst} type="email" label={intl.formatMessage({ defaultMessage: 'Email' })} />
            {emailVerifyStatus ? <Password name="password" label={intl.formatMessage({ defaultMessage: 'Password' })} /> : ''}
          </>
        )}
        {isNousSupport && otpSent === false && (
          <>
            <FormLabel
              sx={{
                color: '#565656',
                fontWeight: 500,
                fontSize: '1rem'
              }}
              required
            >
              {intl.formatMessage({ defaultMessage: 'Select User For OTP' })}
            </FormLabel>
            <FormControl fullWidth>
              <Select
                name="selectedOption"
                label={intl.formatMessage({ defaultMessage: 'Please select Email' })}
                value={selectedOption}
                onChange={handleOptionChange}
              >
                {users?.userEmails?.map((user) => (
                  <MenuItem key={user?.email} value={user?.email}>
                    {user?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        {otpSent && (
          <Password name="login2fa" label={intl.formatMessage({ defaultMessage: 'Enter the code that was just emailed to you' })} />
        )}
        {is2faRequired && !isNousSupport && (
          <Password name="login2fa" label={intl.formatMessage({ defaultMessage: 'Enter the code that was just emailed to you' })} />
        )}
      </Stack>
      {emailVerifyStatus ? (
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <FormControlLabel
              control={<Checkbox checked={checked} onChange={(event) => setChecked(event.target.checked)} name="checked" color="primary" />}
              label={intl.formatMessage({ defaultMessage: 'Keep me logged in' })}
            />
          </Grid>
          <Grid item>
            <Link to="/forgot-password">
              <Typography variant="subtitle1" color="secondary">
                <FormattedMessage defaultMessage="Forgot Password?" />
              </Typography>
            </Link>
          </Grid>
        </Grid>
      ) : (
        ''
      )}

      {error && emailVerifyStatusOther && (
        <Box sx={{ mt: 3 }}>
          <FormHelperText error>{error}</FormHelperText>
        </Box>
      )}
      {emailVerifyStatus ? (
        <Box sx={{ mt: 2 }}>
          <AnimateButton>
            <Button color="secondary" disabled={isSubmitting} fullWidth size="large" type="submit" variant="contained">
              <FormattedMessage defaultMessage="Sign In" />
            </Button>
          </AnimateButton>
        </Box>
      ) : (
        ''
      )}
      {emailVerifyButton ? (
        <Box sx={{ mt: 2 }}>
          <AnimateButton>
            <Button color="secondary" disabled={checkvalidMail} fullWidth size="large" type="submit" variant="contained">
              <FormattedMessage defaultMessage="Enter Your Email" />
            </Button>
          </AnimateButton>
        </Box>
      ) : (
        ''
      )}
    </FormContainer>
  );
};

export default JWTLogin;
